import { useRef, useState } from 'react';
import { UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuthStore } from '@/features/auth/auth-store';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';
import { postReferral } from '@/features/referrals/referrals-api';
import { type ReferralAssignee, useReferralsStore } from '@/features/referrals/referrals-store';
import { useTeammates } from '@/features/referrals/useTeammates';

import { applyPriorityOrder, customerEligibility } from '../customer-eligibility';
import { useProductPriority } from '@/features/settings/useSettings';
import { useToast } from '../toast-context';

interface ReferPopoverProps {
  customer: Customer;
  products: Product[];
}

/** v6: only strong matches are offered — all required rules met AND ≥60%. */
const MIN_MATCH_RATIO = 0.6;

/** v6 rip-assign list cap. */
const MAX_TEAMMATE_RESULTS = 6;

/**
 * The drawer's Refer action, rebuilt to Ryan's v6 two-step flow (SCRUM-338):
 *
 *  Step 1 — product picker: eligibility-scored strong matches (all required
 *  rules met and ≥60% ratio, sorted best-first) with mono "N of N" scores.
 *
 *  Step 2 — compose: "REFERRING TO" header with the staged product and a back
 *  chevron, an optional "Refer to" teammate search (focus-opens a capped
 *  list; selection renders a removable chip; the signed-in user is excluded,
 *  mirroring the backend SelfReferralForbidden guard), an optional note, and
 *  Back / Send referral actions.
 *
 *  Send submits to the referral store (SCRUM-325) — unassigned referrals
 *  route to the default Branch Manager — and fires the titled success toast
 *  (SCRUM-339). The list open/close uses React state with a 150ms blur delay
 *  so option clicks land (the prototype's DOM-toggling pattern, reimplemented
 *  declaratively).
 */
export function ReferPopover({ customer, products }: ReferPopoverProps) {
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Product | null>(null);
  const [assignee, setAssignee] = useState<ReferralAssignee | null>(null);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [note, setNote] = useState('');
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { showToast } = useToast();
  const user = useAuthStore((s) => s.user);
  const submit = useReferralsStore((s) => s.submit);
  const roster = useTeammates();

  const reset = () => {
    setStaged(null);
    setAssignee(null);
    setQuery('');
    setListOpen(false);
    setNote('');
  };

  const { order: priorityOrder } = useProductPriority();
  // Eligibility sort first, then apply the global product priority (SCRUM-46;
  // RT-D: priority injected as a pre-sort over the existing met/total ordering,
  // score computation untouched).
  const options = applyPriorityOrder(
    products
      .map((p) => ({ product: p, e: customerEligibility(customer, p) }))
      .filter(({ e }) => e.forcedAllMet && e.total > 0 && e.met / e.total >= MIN_MATCH_RATIO)
      .sort((a, b) => b.e.met / b.e.total - a.e.met / a.e.total),
    priorityOrder,
    (o) => o.product.id,
  );

  const teammates = roster
    .filter((t) => {
      if (t.email.toLowerCase() === user?.email.toLowerCase()) return false;
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return `${t.name} ${t.role}`.toLowerCase().includes(q);
    })
    .slice(0, MAX_TEAMMATE_RESULTS);

  const send = () => {
    if (!staged) return;
    const input = {
      customerId: customer.id,
      customerName: customer.name,
      productId: staged.id,
      productName: staged.name,
      branch: customer.branch,
      note,
      assignee,
      submittedBy: user?.displayName ?? 'You',
      ...(user?.email ? { submitterEmail: user.email } : {}),
    };
    // SCRUM-343: persist server-side (fire-and-forget — the API resolves the
    // referrer by email and routes null receivers to the Branch-Manager
    // queue); the local store keeps the UI instant + offline-capable. The
    // next session's sync surfaces the server row (id-deduped).
    postReferral(input).catch(() => {
      // Offline preview / API unavailable — the local store row stands alone.
    });
    submit(input);
    setOpen(false);
    reset();
    showToast({
      title: 'Referral sent',
      body: `${customer.name} referred to ${staged.name} — routed to ${
        assignee ? assignee.name : 'the Branch Manager'
      }.`,
      variant: 'success',
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-1.5">
          <UserPlus className="h-3.5 w-3.5" />
          Refer
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] p-0">
        {staged === null ? (
          <>
            <div className="border-border-1 text-text-3 border-b px-3 py-2 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
              Refer to a product
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {options.length === 0 ? (
                <p className="text-text-3 px-3 py-3.5 text-center text-xs italic">
                  No strong matches (60%+) for this customer right now.
                </p>
              ) : (
                options.map(({ product: p, e }) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setStaged(p);
                    }}
                    className="text-text-1 hover:bg-bg-3 hover:text-text-0 flex w-full items-center justify-between gap-2.5 rounded-sm px-2.5 py-[7px] text-left text-[0.8125rem]"
                  >
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    <span className="text-text-3 flex-shrink-0 font-mono text-[0.625rem] font-semibold">
                      {e.met} of {e.total}
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            {/* Step 2 header: back chevron + REFERRING TO eyebrow + product */}
            <div className="border-border-1 bg-bg-3 flex items-start gap-2 border-b px-3 py-2.5">
              <button
                type="button"
                aria-label="Back to products"
                onClick={() => {
                  reset();
                }}
                className="text-text-3 hover:bg-bg-2 hover:text-text-0 mt-px inline-flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-sm"
              >
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="10 4 6 8 10 12" />
                </svg>
              </button>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-text-3 font-mono text-[0.5625rem] font-semibold tracking-[0.08em] uppercase">
                  Referring to
                </span>
                <span className="text-brand-primary inline-flex min-w-0 items-center gap-[5px] text-[0.8125rem] font-semibold">
                  <svg
                    viewBox="0 0 12 12"
                    width="10"
                    height="10"
                    fill="currentColor"
                    aria-hidden="true"
                    className="flex-shrink-0"
                  >
                    <path d="M6 1C3 1 1 3.5 1 6.5C1 9 3 11 6 11c4 0 5-4 5-7V1.5C9.5 1.2 7.5 1 6 1z" />
                  </svg>
                  <span className="truncate">{staged.name}</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2 px-3 pt-2.5 pb-3">
              {/* Refer to (optional) — teammate search / selected chip */}
              <span className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
                Refer to <span className="text-text-3 font-normal normal-case">(optional)</span>
              </span>
              {assignee ? (
                <div className="flex min-h-9 items-center">
                  <span
                    className="rounded-tk-md inline-flex max-w-full items-center gap-[7px] py-[3px] pr-1.5 pl-1"
                    style={{
                      background: 'color-mix(in srgb, var(--cyan) 10%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--cyan) 32%, transparent)',
                    }}
                  >
                    <span className="bg-brand-primary grid h-[22px] w-[22px] flex-none place-items-center rounded-full font-mono text-[0.5625rem] font-bold text-white">
                      {initialsOf(assignee.name)}
                    </span>
                    <span className="flex min-w-0 flex-col leading-[1.2]">
                      <span className="text-text-0 truncate text-[0.8125rem] font-medium">
                        {assignee.name}
                      </span>
                      <span className="text-text-3 text-[0.625rem]">{assignee.role}</span>
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${assignee.name}`}
                      onClick={() => {
                        setAssignee(null);
                        setListOpen(true);
                        setTimeout(() => searchRef.current?.focus(), 0);
                      }}
                      className="text-text-2 hover:text-text-0 ml-0.5 grid h-[18px] w-[18px] flex-none place-items-center rounded-full hover:bg-[color-mix(in_srgb,var(--cyan)_20%,transparent)]"
                    >
                      <svg
                        viewBox="0 0 12 12"
                        width="8"
                        height="8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M2 2l8 8M10 2l-8 8" />
                      </svg>
                    </button>
                  </span>
                </div>
              ) : (
                <div className="relative">
                  <div className="border-border-1 rounded-tk-md bg-bg-4 flex min-h-9 items-center gap-[7px] border px-2.5 py-1.5 focus-within:border-[color-mix(in_srgb,var(--cyan)_45%,var(--border-1))] focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--cyan)_9%,transparent)]">
                    <svg
                      viewBox="0 0 16 16"
                      width="13"
                      height="13"
                      fill="none"
                      stroke="var(--text-3)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      aria-hidden="true"
                      className="flex-shrink-0"
                    >
                      <circle cx="6.5" cy="5" r="2.6" />
                      <path d="M2 13.5c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
                      <path d="M12.5 5v4M10.5 7h4" />
                    </svg>
                    <input
                      ref={searchRef}
                      value={query}
                      placeholder="Search a teammate…"
                      aria-label="Search a teammate"
                      onChange={(e) => {
                        setQuery(e.target.value);
                      }}
                      onFocus={() => {
                        if (blurTimer.current) clearTimeout(blurTimer.current);
                        setListOpen(true);
                      }}
                      onBlur={() => {
                        // 150ms delay so option mousedown/click lands before
                        // the list hides (v6 rip-assign pattern, React state).
                        blurTimer.current = setTimeout(() => {
                          setListOpen(false);
                        }, 150);
                      }}
                      className="text-text-0 placeholder:text-text-3 min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
                    />
                  </div>
                  {listOpen && (
                    <div className="border-border-1 bg-bg-4 rounded-tk-md absolute right-0 left-0 z-10 mt-1 max-h-[184px] overflow-y-auto border p-1 shadow-[0_6px_18px_rgba(0,0,0,0.12)]">
                      {teammates.length === 0 ? (
                        <p className="text-text-3 p-2.5 text-center text-xs">
                          No teammates match “{query.trim()}”.
                        </p>
                      ) : (
                        teammates.map((t) => (
                          <button
                            key={t.email}
                            type="button"
                            onClick={() => {
                              if (blurTimer.current) clearTimeout(blurTimer.current);
                              setAssignee(t);
                              setListOpen(false);
                              setQuery('');
                            }}
                            className="hover:bg-bg-3 flex w-full items-center gap-[9px] rounded-md px-2 py-1.5 text-left transition-colors"
                          >
                            <span className="text-brand-primary grid h-[26px] w-[26px] flex-none place-items-center rounded-full font-mono text-[0.625rem] font-bold [background:color-mix(in_srgb,var(--cyan)_14%,transparent)]">
                              {initialsOf(t.name)}
                            </span>
                            <span className="flex min-w-0 flex-col leading-[1.25]">
                              <span className="text-text-0 text-[0.8125rem]">{t.name}</span>
                              <span className="text-text-3 truncate text-[0.6875rem]">
                                {t.role}
                              </span>
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Add a note (optional) */}
              <span className="text-text-3 mt-1 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
                Add a note <span className="font-normal normal-case">(optional)</span>
              </span>
              <textarea
                value={note}
                maxLength={2048}
                aria-label="Add a note"
                placeholder="Context for the officer — what the customer asked about, signals you saw at the counter…"
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                className="bg-bg-3 border-border-1 rounded-tk-md text-text-0 placeholder:text-text-3 min-h-[78px] w-full resize-y border px-2.5 py-2 text-[0.8125rem] outline-none focus:border-[color-mix(in_srgb,var(--cyan)_45%,var(--border-1))] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--cyan)_9%,transparent)]"
              />

              <div className="mt-0.5 flex justify-end gap-1.5">
                <Button
                  variant="outline"
                  className="px-3 py-[7px] text-xs"
                  onClick={() => {
                    reset();
                  }}
                >
                  Back
                </Button>
                <Button className="px-3.5 py-[7px] text-xs" onClick={send}>
                  Send referral
                </Button>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** First letters of the first two name words ("Carol Whitfield" → "CW"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase();
}
